<template>
  <div class="share-callback-page" :class="{ 'mobile': isMobile, 'desktop': !isMobile }">
    <div v-if="state === 'loading'" class="state-block">
      <h1>パートナー接続中…</h1>
      <p>少々お待ちください。</p>
    </div>
    <div v-else-if="state === 'success'" class="state-block">
      <h1>接続が完了しました</h1>
      <p>パートナーとの共有が有効になりました。</p>
      <p class="hint">LINEにメノアからのメッセージが届いているかご確認ください。</p>
    </div>
    <div v-else class="state-block error-block">
      <h1>{{ errorTitle }}</h1>
      <p>{{ errorMessage }}</p>
      <button v-if="canRetry" @click="retry" class="btn-primary">もう一度試す</button>
    </div>
  </div>
</template>

<script>
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/firebase'

export default {
  name: 'ShareCallbackView',
  data() {
    return {
      isMobile: false,
      state: 'loading',
      errorTitle: '',
      errorMessage: '',
      canRetry: false,
      tokenId: '',
      inviterUid: ''
    }
  },
  mounted() {
    document.title = 'パートナー接続 | メノア'
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    this.handleCallback()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    async handleCallback() {
      // build 時に env 未設定だと redirectUri が組み立てられず callable 呼出に失敗するため、
      // 入口で early-return する (menoah-mobile-app-flutter#365)
      if (!process.env.VUE_APP_WEB_ORIGIN) {
        this.setError('config', 'サイト設定に問題があります', '管理者にお問い合わせください。', false)
        return
      }
      const { code, state, error } = this.$route.query

      if (error === 'access_denied') {
        this.setError('user_cancel', 'LINE 認証がキャンセルされました', 'パートナーと連携するには LINE での認証が必要です。', true)
        this.clearSession()
        return
      }
      if (error) {
        this.setError('line_auth_callback', '認証エラー', `LINE 認証に失敗しました (${error})`, true)
        this.clearSession()
        return
      }
      if (!code || !state) {
        this.setError('incomplete_request', 'リクエストが不完全です', '招待フローを最初からやり直してください。', false)
        return
      }

      // tokenId / inviterUid は state (URL) から復元するのが基本。
      // LINE 内ブラウザは OAuth 往復で sessionStorage を初期化するため (#12)。
      let tokenId, inviterUid
      const parsed = this.parseState(state)
      if (parsed) {
        tokenId = parsed.tokenId
        inviterUid = parsed.inviterUid
        // CSRF: cookie に退避した nonce があれば照合（消えていれば tokenId 秘匿で担保）
        const cookieNonce = this.readCsrfNonce()
        if (cookieNonce && cookieNonce !== parsed.nonce) {
          this.setError('session_expired', 'セッションが切れました', 'アプリで招待リンクを再発行してください。', false)
          this.clearSession()
          return
        }
      } else {
        // 後方互換: 旧リンクやデプロイ移行中は sessionStorage から復元
        const savedNonce = sessionStorage.getItem('share_state_nonce')
        tokenId = sessionStorage.getItem('share_state_tokenId')
        inviterUid = sessionStorage.getItem('share_state_inviterUid')
        if (!savedNonce || !tokenId || state !== savedNonce) {
          this.setError('session_expired', 'セッションが切れました', 'アプリで招待リンクを再発行してください。', false)
          this.clearSession()
          return
        }
      }
      if (!tokenId || !/^[0-9a-f]{64}$/.test(tokenId)) {
        this.setError('token_invalid', 'リンクが無効です', '招待リンクが正しくありません。', false)
        this.clearSession()
        return
      }
      this.tokenId = tokenId
      this.inviterUid = inviterUid || ''

      const origin = process.env.VUE_APP_WEB_ORIGIN
      const redirectUri = `${origin}/share/callback`
      try {
        const consume = httpsCallable(functions, 'consumeInviteToken')
        const res = await consume({ tokenId, code, redirectUri })
        if (res && res.data && res.data.success === true) {
          this.state = 'success'
          this.clearSessionAll()
          return
        }
        this.setError('unknown', '接続に失敗しました', '時間をおいてもう一度お試しください。', false)
      } catch (e) {
        this.handleHttpsError(e)
      }
    },
    handleHttpsError(e) {
      const code = (e && e.code) || ''
      switch (code) {
        case 'functions/not-found':
          this.setError('token_invalid', 'リンクが無効か期限切れです', '招待リンクの有効期限は 24 時間です。アプリで再発行してください。', false)
          break
        case 'functions/failed-precondition':
          this.setError('token_used', 'このリンクは既に使用されています', '同じ招待リンクは一度しか使えません。アプリで新しいリンクを発行してください。', false)
          break
        case 'functions/already-exists':
          this.setError('already_paired', '既にパートナーが登録されています', 'パートナー共有は 1 名のみ可能です。既存のパートナーを解除してから再度お試しください。', false)
          break
        case 'functions/unauthenticated':
          this.setError('line_auth_function', 'LINE 認証に失敗しました', '時間をおいてもう一度お試しください。', true)
          break
        case 'functions/resource-exhausted':
          this.setError('rate_limit', '混雑しています', 'しばらく待ってからもう一度お試しください。', true)
          break
        case 'functions/internal':
        default:
          this.setError('network', '通信エラーが発生しました', '電波状況を確認してもう一度お試しください。', true)
          break
      }
      this.clearSession()
    },
    setError(stateKey, title, message, retry) {
      this.state = `error_${stateKey}`
      this.errorTitle = title
      this.errorMessage = message
      this.canRetry = retry
    },
    retry() {
      const tokenId = this.tokenId || sessionStorage.getItem('share_state_tokenId')
      const inviterUid = this.inviterUid || sessionStorage.getItem('share_state_inviterUid')
      if (tokenId && inviterUid) {
        this.$router.replace({ path: '/share', query: { q: inviterUid, r: tokenId } })
      } else {
        this.$router.replace('/')
      }
    },
    parseState(state) {
      // Share.vue が組んだ state: nonce(32hex) + tokenId(64hex) + inviterUid(16進)。
      // 旧形式 (nonce のみ 32hex) は length 不足で null を返し sessionStorage へフォールバック。
      if (typeof state !== 'string' || !/^[0-9a-f]+$/.test(state)) return null
      if (state.length < 96 || (state.length - 96) % 2 !== 0) return null
      const tokenId = state.slice(32, 96)
      if (!/^[0-9a-f]{64}$/.test(tokenId)) return null
      const nonce = state.slice(0, 32)
      const hex = state.slice(96)
      let inviterUid = ''
      try {
        for (let i = 0; i < hex.length; i += 2) {
          inviterUid += String.fromCharCode(parseInt(hex.slice(i, i + 2), 16))
        }
      } catch (e) {
        inviterUid = ''
      }
      return { nonce, tokenId, inviterUid }
    },
    readCsrfNonce() {
      try {
        const m = document.cookie.match(/(?:^|;\s*)share_csrf_nonce=([0-9a-f]+)/)
        return m ? m[1] : ''
      } catch (e) {
        return ''
      }
    },
    clearCsrfNonce() {
      try {
        document.cookie = 'share_csrf_nonce=; Max-Age=0; Path=/; SameSite=Lax; Secure'
      } catch (e) {
        // noop
      }
    },
    clearSession() {
      try {
        sessionStorage.removeItem('share_state_nonce')
      } catch (e) {
        // noop
      }
      this.clearCsrfNonce()
    },
    clearSessionAll() {
      try {
        sessionStorage.removeItem('share_state_nonce')
        sessionStorage.removeItem('share_state_tokenId')
        sessionStorage.removeItem('share_state_inviterUid')
      } catch (e) {
        // noop
      }
      this.clearCsrfNonce()
    }
  }
}
</script>

<style scoped>
.state-block { text-align: center; }
.state-block h1 { color: var(--color-green-600); }
.state-block p { color: var(--color-grey-500); margin-top: 1rem; }
.error-block h1 { color: var(--color-grey-500); }
.hint { font-size: 14px; color: var(--color-grey-400); }
.btn-primary {
  margin-top: 1.5rem;
  background-color: var(--color-yellow-500);
  color: var(--color-green-600);
  border: none;
  border-radius: 25px;
  padding: 12px 32px;
  font-weight: bold;
  cursor: pointer;
}
.mobile { padding: 56px 24px; }
.mobile h1 { font-size: 24px; margin-bottom: 20px; }
.desktop { padding: 120px 80px; max-width: 1120px; margin: 0 auto; }
.desktop h1 { font-size: 28px; padding-bottom: 40px; }
</style>
